import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { ImportColumnConfig } from '../../interfaces/import-config.interface';
import { ErrorMessageService } from '../../services/error-message.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface RowErrorsDialogData {
  rowIndex: number;
  rowData: any;
  columns: ImportColumnConfig[];
  errors: { [columnKey: string]: any };
}

interface ColumnError {
  columnKey: string;
  columnName: string;
  errorKey: string;
  errorMessage: string;
  cellValue: any;
  severity: 'error' | 'warning';
}

@Component({
  selector: 'app-row-errors-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatListModule
  ],
  template: `
      <div mat-dialog-title class="flex items-center gap-3">
        <mat-icon class="text-red-600">error</mat-icon>
        <span>Errores en Fila {{ data.rowIndex + 1 }}</span>
      </div>

      <mat-dialog-content >
        <div class="space-y-4">
          <!-- Error Summary -->
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-center gap-2 text-red-800 font-medium mb-2">
              <mat-icon class="w-5 h-5">warning</mat-icon>
              <span>{{ columnErrors.length }} {{ columnErrors.length === 1 ? 'error encontrado' : 'errores encontrados' }}</span>
            </div>
            <p class="text-sm text-red-700">
              Corrige los siguientes errores antes de continuar con la importación:
            </p>
          </div>

          <!-- Individual Errors -->
          <div class="space-y-3">
            <div 
              *ngFor="let error of columnErrors; trackBy: trackByError"
              class="border rounded-lg p-4"
              [class.border-red-200]="error.severity === 'error'"
              [class.border-yellow-200]="error.severity === 'warning'"
              [class.bg-red-50]="error.severity === 'error'"
              [class.bg-yellow-50]="error.severity === 'warning'">
              
              <!-- Error Header -->
              <div class="flex items-start gap-3">
                <mat-icon 
                  [class.text-red-600]="error.severity === 'error'"
                  [class.text-yellow-600]="error.severity === 'warning'"
                  class="w-5 h-5 mt-0.5">
                  {{ error.severity === 'error' ? 'info' : 'info' }}
                </mat-icon>
                
                <div class="flex-1">
                  <!-- Column Name -->
                  <h4 class="font-medium text-gray-900 mb-1">{{ error.columnName }}</h4>
                  
                  <!-- Error Message -->
                  <p 
                    class="text-sm mb-2"
                    [class.text-red-800]="error.severity === 'error'"
                    [class.text-yellow-800]="error.severity === 'warning'">
                    {{ error.errorMessage }}
                  </p>
                  
                  <!-- Current Value -->
                  <div class="text-xs text-gray-600">
                    <span class="font-medium">Valor actual:</span>
                    <span class="ml-1 px-2 py-1 bg-gray-100 rounded font-mono">
                      {{ error.cellValue || '(vacío)' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Help Text -->
          <div *ngIf="hasHelpText()" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-center gap-2 text-blue-800 font-medium mb-2">
              <mat-icon class="w-5 h-5">help_outline</mat-icon>
              <span>Ayuda adicional</span>
            </div>
            <div class="space-y-2 text-sm text-blue-800">
              <div *ngFor="let help of getHelpTexts()">
                <strong>{{ help.columnName }}:</strong> {{ help.helpText }}
              </div>
            </div>
          </div>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions class="flex justify-between p-4">
        <button mat-button (click)="close()">
          Cerrar
        </button>
        <div class="flex gap-2">
          <button mat-stroked-button color="primary" (click)="copyErrorsToClipboard()">
            <mat-icon>content_copy</mat-icon>
            Copiar Errores
          </button>
          <button mat-raised-button color="primary" (click)="close()">
            Entendido
          </button>
        </div>
      </mat-dialog-actions>
  `,
  styles: [`
    .row-errors-dialog {
      min-width: 500px;
      max-width: 600px;
    }
    
    .space-y-4 > * + * {
      margin-top: 1rem;
    }
    
    .space-y-3 > * + * {
      margin-top: 0.75rem;
    }
    
    .space-y-2 > * + * {
      margin-top: 0.5rem;
    }
  `]
})
export class RowErrorsDialogComponent {
  columnErrors: ColumnError[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RowErrorsDialogData,
    private dialogRef: MatDialogRef<RowErrorsDialogComponent>,
    private errorMessageService: ErrorMessageService,
    private snackBar: MatSnackBar
  ) {
    this.processErrors();
  }

  private processErrors(): void {
    this.columnErrors = [];

    Object.entries(this.data.errors).forEach(([columnKey, errorObj]) => {
      const column = this.data.columns.find(col => col.key === columnKey);
      if (!column || !errorObj) return;

      // Handle multiple error types for the same column
      Object.entries(errorObj).forEach(([errorKey, errorValue]) => {
        if (errorKey.startsWith('_')) return; // Skip internal properties

        let errorMessage: string;
        let severity: 'error' | 'warning';

        // For pre-validation errors, use backend message and severity directly
        if (errorKey.startsWith('preValidation')) {
          errorMessage = (errorValue as any)?.message || 'Error en pre-validación';
          severity = (errorValue as any)?.severity || 'error';
        } else {
          // For regular validation errors, use the mapping service
          errorMessage = this.errorMessageService.getErrorMessage(
            errorKey,
            column,
            errorValue
          );
          severity = this.getErrorSeverity(errorKey);
        }

        this.columnErrors.push({
          columnKey,
          columnName: column.displayName,
          errorKey,
          errorMessage,
          cellValue: this.data.rowData[columnKey],
          severity
        });
      });
    });

    // Sort errors by severity (errors first, then warnings)
    this.columnErrors.sort((a, b) => {
      if (a.severity === 'error' && b.severity === 'warning') return -1;
      if (a.severity === 'warning' && b.severity === 'error') return 1;
      return a.columnName.localeCompare(b.columnName);
    });
  }

  private getErrorSeverity(errorKey: string): 'error' | 'warning' {
    // Define which errors are warnings vs hard errors
    const warningErrors = ['maxlength', 'pattern'];
    return warningErrors.includes(errorKey) ? 'warning' : 'error';
  }

  hasHelpText(): boolean {
    return this.columnErrors.some(error => {
      const column = this.data.columns.find(col => col.key === error.columnKey);
      return column?.helpText;
    });
  }

  getHelpTexts(): { columnName: string; helpText: string }[] {
    const helpTexts: { columnName: string; helpText: string }[] = [];
    
    this.columnErrors.forEach(error => {
      const column = this.data.columns.find(col => col.key === error.columnKey);
      if (column?.helpText) {
        const existing = helpTexts.find(h => h.columnName === column.displayName);
        if (!existing) {
          helpTexts.push({
            columnName: column.displayName,
            helpText: column.helpText
          });
        }
      }
    });

    return helpTexts;
  }

  trackByError(index: number, error: ColumnError): string {
    return `${error.columnKey}-${error.errorKey}`;
  }

  async copyErrorsToClipboard(): Promise<void> {
    const errorText = this.columnErrors
      .map(error => `${error.columnName}: ${error.errorMessage}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(errorText);
      // Could show a toast notification here
    } catch (err) {
      this.snackBar.open(err.error.message || "Fallo para copiar en clipboard.","Cerrar",{ duration: 5000});
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}